import axios from '@/api/axios-date-selection2.js'
import calendarAxios from '@/api/axios-calendar'
import moment from 'moment'
import { auspiciousTypes } from '@/constants/date-selection/date-selection-consts'
import timeOfDays from '@/constants/date-selection/time-of-days'

const state = {
  isLoading: true,
  isSubmitting: false,
  formErrorMessage: null,
  dateSelectionDetails: {},
  editable: true,
}

const mutations = {
  setDateSelectionDetails(state, dateSelectionDetails) {
    state.dateSelectionDetails = dateSelectionDetails
    state.isLoading = false
  },
  setLoading(state, value) {
    state.isLoading = value
  },
  setSubmitting(state, value) {
    if (value) {
      state.formErrorMessage = null
    }
    state.isSubmitting = value
  },
  setFormErrorMessage(state, value) {
    state.formErrorMessage = value
  },
  setEditable(state, value) {
    state.editable = value
  },
}

const actions = {
  async loadDateSelectionDetails({ commit }, { id }) {
    commit('setLoading', true)
    try {
      const response = await axios.get(`/auspicious-dates/${id}`)
      const json = response.data
      const members = getMembers(json)
      await enrichMembersWithBaziData(members)

      const owners = {
        label: getLabelByType(json.type),
        members,
      }

      const confirmationInfo = parseConfirmationInfo(json)
      const dateSelectionDetails = await formatDateSelectionDetails(
        json,
        owners,
        confirmationInfo
      )
      const editableStatus = ['date_selecting', 'auto_date_selecting']
      let editable = editableStatus.includes(json.status)

      commit('setEditable', editable)
      commit('setDateSelectionDetails', dateSelectionDetails)
    } catch (error) {
      console.error('Error loading date selection details:', error)
    } finally {
      commit('setLoading', false)
    }
  },
  async submitDateSelectionsForm({ commit }, request) {
    commit('setSubmitting', true)
    try {
      const { id } = request
      const parsedRequestBody = parseRequest(request)
      await axios.post(
        `/auspicious-dates/${id}/auspicious-dates`,
        parsedRequestBody
      )
    } catch (error) {
      commit('setFormErrorMessage', `${error}`)
    }
    commit('setSubmitting', false)
  },
  setEditable({ commit }, editable) {
    commit('setEditable', editable)
  },
}

const getters = {
  dateSelectionDetailsIsLoading: (state) => state.isLoading,
  dateSelectionDetails: (state) => state.dateSelectionDetails,
  isDateSelectionFormSubmitting: (state) => state.isSubmitting,
  formErrorMessage: (state) => state.formErrorMessage,
  editableDateSelection: (state) => state.editable,
}

const parseRequest = (request) => {
  if (request.requestType === 'new_car') {
    return {
      remark: request.suggestion,
      is_auto_select: true,
      new_car: {
        lucky_colors: [
          request.selectedColors.auspiciousOne.id,
          request.selectedColors.auspiciousTwo.id,
        ].filter(Boolean),
        lucky_numbers: request.selectedNumbers.filter(Boolean),
        auspicious_dates: request.selectedDates.map((it) => {
          return {
            [it.key]: {
              date: moment(it.start_date_time).format('yyyy-MM-DD'),
              start_time: moment(it.start_date_time).format('HH:mm'),
              end_time: moment(it.end_date_time).format('HH:mm'),
            },
          }
        }),
      },
    }
  }

  const ALLOW_ONE_DATE = ['beauty', 'study_abroad', 'new_pet']
  if (ALLOW_ONE_DATE.includes(request.requestType)) {
    return {
      remark: request.suggestion,
      is_auto_select: true,
      [request.requestType]: {
        auspicious_date: request.selectedDates.map((it) => {
          return {
            [it.key]: {
              date: moment(it.start_date_time).format('yyyy-MM-DD'),
              start_time: moment(it.start_date_time).format('HH:mm'),
              end_time: moment(it.end_date_time).format('HH:mm'),
            },
          }
        })[0],
      },
    }
  }

  // cosmetic_surgery, birth
  return {
    remark: request.suggestion,
    is_auto_select: true,
    [request.requestType]: {
      auspicious_dates: request.selectedDates.map((it) => ({
        [it.key]: {
          date: moment(it.start_date_time).format('yyyy-MM-DD'),
          start_time: moment(it.start_date_time).format('HH:mm'),
          end_time: moment(it.end_date_time).format('HH:mm'),
        },
      })),
    },
  }
}

const getMembers = (json) => {
  const typeDetails = json[json.type]?.detail || {}
  const structureKeys = [
    'members',
    'bride',
    'bride_father',
    'bride_mother',
    'groom',
    'groom_father',
    'groom_mother',
    'father',
    'mother',
    'person',
  ]

  const formatMember = (member, title) => {
    if (member?.date_of_birth) {
      return {
        title,
        name: `${member.first_name} ${member.last_name}`,
        dob: member.date_of_birth,
        tob: member.time_of_birth || null,
        extra: formatExtraInfo(member),
      }
    }
    return null
  }

  const formatExtraInfo = (member) => {
    const notAllowedKeys = [
      'first_name',
      'last_name',
      'date_of_birth',
      'time_of_birth',
    ]
    const extraInfo = Object.entries(member)
      .filter(([key]) => !notAllowedKeys.includes(key))
      .map(([key, value]) => ({
        label: formatKeyToLabel(key),
        key,
        value,
      }))
    return extraInfo.length ? extraInfo : undefined
  }

  const formatKeyToLabel = (key) =>
    key
      .split('_')
      .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
      .join(' ')

  const formatTitleByKey = (key, type) => {
    const typeMap = {
      new_pet: 'Pet owner',
      study_abroad: 'Student',
    }

    if (Object.keys(typeMap).includes(type)) {
      return typeMap[type]
    }

    const titleMap = {
      father: 'Father',
      mother: 'Mother',
      bride: 'Bride',
      bride_father: "Bride's Father",
      bride_mother: "Bride's Mother",
      groom: 'Groom',
      groom_father: "Groom's Father",
      groom_mother: "Groom's Mother",
      person: 'Patient',
    }
    return titleMap[key] || key
  }

  const formatTitleByType = (type, index) => {
    const titleMap = {
      new_car: 'Car Own',
      product_launch: 'Business Owner ' + (index + 1),
    }
    return titleMap[type] || formatKeyToLabel(type)
  }

  const members = structureKeys
    .filter((key) => typeDetails[key])
    .flatMap((key) => {
      const data = typeDetails[key]
      if (Array.isArray(data)) {
        return data
          .map((item, index) =>
            formatMember(item, formatTitleByType(json.type, index))
          )
          .filter(Boolean)
      } else {
        return [formatMember(data, formatTitleByKey(key, json.type))]
      }
    })
    .filter(Boolean) // remove empty object

  if (json.type === 'birth') {
    members.push({
      title: 'Baby',
      extra: [
        {
          label: 'Year of Birth',
          value: typeDetails.baby_year_of_birth,
          key: 'baby_date_of_birth',
        },
        { label: 'Gender', value: typeDetails.baby_gender, key: 'baby_gender' },
      ],
    })
  }

  if (json.type === 'new_pet') {
    members[0].extra = [
      {
        label: 'Gender',
        value: typeDetails.person_gender
          ? typeDetails.person_gender.charAt(0).toUpperCase() +
            typeDetails.person_gender?.slice(1)
          : '-',
        key: 'gender',
      },
      {
        label: 'Chinese Time of Birth',
        value: typeDetails.pet_info.chinese_dob
          ? typeDetails.pet_info.chinese_dob
          : '-',
        key: 'chinese_dob',
      },
    ]

    members.unshift({
      title: 'Pet',
      name: typeDetails.pet_info?.name || '-',
      dob: typeDetails.pet_info?.date_of_birth || '-',
      tob: typeDetails.pet_info?.time_of_birth
        ? typeDetails.pet_info.time_of_birth
        : '-',
      extra: [
        {
          label: 'Gender',
          value:
            typeDetails.pet_info.gender.charAt(0).toUpperCase() +
            typeDetails.pet_info.gender?.slice(1),
          key: 'gender',
        },
        {
          label: 'Chinese Time of Birth',
          value: typeDetails.pet_info?.chinese_dob || '-',
          key: 'chinese_dob',
        },
      ],
    })
  }

  members.map((e) => {
    if (typeof e.extra == 'undefined') {
      return e
    }
    let index = e.extra.findIndex((e) => e.key == 'chinese_dob')
    if (index < 0) {
      return e
    }
    let newExtra = e.extra || '-'
    newExtra[index].label = 'Chinese Time of Birth'
    return { ...e, extra: newExtra }
  })

  return members
}

const enrichMembersWithBaziData = async (members) => {
  for (const member of members) {
    if (!member.dob) continue

    member.tob = isValidTimeFormat(member.tob) ? member.tob : null
    try {
      let path =
        `/bazis/${member.dob}` + (member.tob ? `?time=${member.tob}` : '')
      const res = await calendarAxios.get(path)
      const data = res.data
      member.fiveStructure = await getFiveStructure(member.dob, member.tob)
      member.heavenly = getHeavenlyData(data)
      member.earthly = getEarthlyData(data)
    } catch (error) {
      console.error('Error fetching Bazi data:', error)
    }
  }
}

const isValidTimeFormat = (timeString) =>
  moment(timeString, 'HH:mm', true).isValid()

const getFiveStructure = async (dob, tob) => {
  const response = await calendarAxios.get(
    `/bazis/${dob}/five-structure` + (tob ? `?time=${tob}` : '')
  )
  const { earth, fire, metal, water, wood } = response.data
  // ['Metal', 'Water', 'Wood', 'Fire', 'Earth']
  return [metal, water, wood, fire, earth]
}

const getHeavenlyData = (data) =>
  ['hour', 'day', 'month', 'year'].map((slot) => ({
    title: data[slot]?.heavenly_stem?.mandarin || '',
    subtitle: `${data[slot]?.heavenly_stem?.element?.mandarin || ''} ${data[slot]?.heavenly_stem?.element?.name_en || ''}`,
    description: data[slot]?.heavenly_stem?.pinyin || '',
    labelEn: data[slot]?.heavenly_stem?.ten_god?.name_en_abbr || '',
    labelZh: data[slot]?.heavenly_stem?.ten_god?.mandarin || '',
  }))

const getEarthlyData = (data) =>
  ['hour', 'day', 'month', 'year'].map((slot) => ({
    title: data[slot]?.earthly_branch?.mandarin || '',
    subtitle: `${data[slot]?.earthly_branch?.element?.mandarin || ''} ${data[slot]?.earthly_branch?.element?.name_en || ''}`,
    description: data[slot]?.earthly_branch?.name || '',
  }))

const getLabelByType = (type) =>
  ({
    new_car: "Car Owner's Information",
    birth: "Family's Information",
    cosmetic_surgery: "Patient's Information",
    beauty: "Patient's Information",
    new_pet: "Pet's Information",
    study_abroad: "Student's Information",
    product_launch: "Business owner's Information",
  })[type] || "Requester's Information"

const parseConfirmationInfo = (data) => {
  const babyInfo = data[data.type]['baby_info']
  const carInfo = data[data.type]['actual_date']?.car_purchasing
  const surgeryInfo = data[data.type]?.current_auspicious_date?.cosmetic_surgery
  const studyInfo = data[data.type]?.auspicious_date?.study_abroad
  const petInfo = data[data.type]?.auspicious_date?.new_pet
  const productLaunchInfo =
    data[data.type]?.current_auspicious_date?.product_launch

  const info = {
    new_car: { title: 'Date of Car Purchase', label: 'Date of Purchase' },
    birth: { title: "Baby's Date of Birth", label: 'Date of Birth' },
    cosmetic_surgery: { title: 'Date of Surgery', label: 'Date of Surgery' },
    study_abroad: {
      title: 'Date of Travel to Study Abroad',
      label: 'Date of Travel to Study Abroad',
    },
    new_pet: { title: 'Date of Pet Adoption', label: 'Date of Pet Adoption' },
    product_launch: {
      title: 'Date of Product Launching',
      label: 'Date of Product Launching',
    },
  }[data?.type]

  if (data.status === 'date_confirming') {
    return {
      ...info,
      isConfirmed: false,
    }
  }

  if (data?.type === 'birth' && babyInfo) {
    const formattedDate = moment(babyInfo.date_of_birth).format('DD MMM YYYY')
    let formattedTime = ''

    if (babyInfo.time_of_birth) {
      formattedTime = ` • ${moment(babyInfo.time_of_birth, 'HH:mm').format('hh:mmA')}`
    }

    return {
      ...info,
      babyName: `${babyInfo.first_name} ${babyInfo.last_name}`,
      value: `${formattedDate}${formattedTime}`,
      isConfirmed: true,
    }
  }

  if (data?.type === 'new_car' && carInfo) {
    const formattedDate = moment(carInfo.date).format('DD MMM YYYY')
    const startTime = moment(carInfo.start_time, 'HH:mm').format('hh:mmA')
    const endTime = moment(carInfo.end_time, 'HH:mm').format('hh:mmA')

    return {
      ...info,
      value: `${formattedDate} | ${startTime} - ${endTime}`,
      isConfirmed: true,
    }
  }

  if (data?.type === 'cosmetic_surgery' && surgeryInfo) {
    const formattedDate = moment(surgeryInfo.date).format('DD MMM YYYY')
    const startTime = moment(surgeryInfo.start_time, 'HH:mm').format('hh:mmA')
    const endTime = moment(surgeryInfo.end_time, 'HH:mm').format('hh:mmA')

    return {
      ...info,
      value: `${formattedDate} | ${startTime} - ${endTime}`,
      isConfirmed: true,
    }
  }

  if (data?.type === 'study_abroad' && studyInfo) {
    const formattedDate = moment(studyInfo.date).format('DD MMM YYYY')
    const startTime = moment(studyInfo.start_time, 'HH:mm').format('hh:mmA')
    const endTime = moment(studyInfo.end_time, 'HH:mm').format('hh:mmA')

    return {
      ...info,
      value: `${formattedDate} | ${startTime} - ${endTime}`,
      isConfirmed: true,
    }
  }

  if (data?.type === 'new_pet' && petInfo) {
    const formattedDate = moment(petInfo.date).format('DD MMM YYYY')
    const startTime = moment(petInfo.start_time, 'HH:mm').format('hh:mmA')
    const endTime = moment(petInfo.end_time, 'HH:mm').format('hh:mmA')

    return {
      ...info,
      value: `${formattedDate} | ${startTime} - ${endTime}`,
      isConfirmed: true,
    }
  }

  if (data?.type === 'product_launch' && productLaunchInfo) {
    const formattedDate = moment(productLaunchInfo.date).format('DD MMM YYYY')
    const startTime = moment(productLaunchInfo.start_time, 'HH:mm').format(
      'hh:mmA'
    )
    const endTime = moment(productLaunchInfo.end_time, 'HH:mm').format('hh:mmA')

    return {
      ...info,
      value: `${formattedDate} | ${startTime} - ${endTime}`,
      isConfirmed: true,
    }
  }

  return null
}

const parseAuspiciousDate = (json, key) => {
  if (json[json.type][key] && !Array.isArray(json[json.type][key])) {
    // convert single array
    json[json.type][key] = [json[json.type][key]]
    return parseAuspiciousDates(json, key)
  }

  return null
}

const parseAuspiciousDates = (json, key) => {
  let parsedDates = []
  const details = json[json.type]
  if (details && details[key] && details[key].length > 0) {
    const supportedDetailKeys = [
      'beauty_detail',
      'cosmetic_surgery_detail',
      'study_abroad_detail',
      'new_pet_detail',
      'product_launch_detail',
    ]
    json[json.type][key].forEach((dateEntry) => {
      const keys = Object.keys(dateEntry)
      const detailKey = keys.filter((it) => supportedDetailKeys.includes(it))[0]
      // assume key as auspicious-date's key except `supportedDetailKeys`
      const key = keys.filter((it) => !supportedDetailKeys.includes(it))[0]

      if (key && dateEntry[key]) {
        const data = dateEntry[key]
        const auspiciousStartDateTime = `${data.auspicious_date.date}T${data.auspicious_date.start_time}`
        const auspiciousEndDateTime = `${data.auspicious_date.date}T${data.auspicious_date.end_time}`
        const title = `Auspicious date for ${json.type == 'study_abroad' ? 'travel to ' : ''}${auspiciousTypes.find((it) => it.value === json.type).en.toLowerCase()}`

        const parsedEntry = {
          title,
          start_date_time: auspiciousStartDateTime,
          end_date_time: auspiciousEndDateTime,
          key,
          officer: data.officer || '',
          constellation: {
            name: data.constellation || '',
            types: data.tags || [],
          },
          result_type: data.result_type || '',
          detail: dateEntry[detailKey],
        }

        parsedDates.push(parsedEntry)
      }
    })
  }

  return parsedDates
}

const parseSurgeryInfo = (json) => {
  return {
    decorationInfo: json[json.type].decoration_suggestion,
    nutritionInfo: json[json.type].nutrition_suggestion,
  }
}

const parseStudyInfo = (json) => {
  return {
    accessoriesInfo: json[json.type].accessories_suggestion,
    description:
      'ใช้สิ่งของเหล่านี้จะช่วยเสริมให้การเรียนของคุณให้ราบรื่นยิ่งขึ้น',
  }
}

const parseStudyGift = (json) => {
  return {
    from_country_code: json[json.type].from_country_code || '',
    to_country_code: json[json.type].to_country_code || '',
    institute_image_url: json[json.type].institute_image_url || '',
    institute_name: json[json.type].institute_name || '',
  }
}

const parsePetInfo = (json) => {
  return {
    petNameSuggestionInfo: json[json.type].pet_name_suggestion,
  }
}

const parsePetGift = (json) => {
  let compatibilityTypeString = json[json.type]?.compatibility_type
  let description = compatibilityTypeString
    ? `Compatibility for ${compatibilityTypeString?.charAt(0).toUpperCase() + compatibilityTypeString?.slice(1)}`
    : ''
  return {
    pet_name: json[json.type]?.detail?.pet_info?.name || '',
    pet_gender: json[json.type]?.detail?.pet_info?.gender || '',
    compatibility_description: compatibilityTypeString ? description : '',
    pet_image_url: json[json.type]?.pet_image_url || '',
  }
}

const parseProductInfo = (json) => {
  return {
    decorationsInfo: json[json.type].decoration_suggestion,
    compatibleZodiacsInfo: json[json.type].compatible_zodiacs,
  }
}

const parseProductGift = (json) => {
  return {
    shop_name: json[json.type].shop_name || '',
    product_name: json[json.type].product_name || '',
    product_image_url: json[json.type].product_image_url || '',
  }
}

const parseSurgeryTypeInfo = (json) => {
  const details = json[json.type].detail
  const type = details.beauty_procedure || details.cosmetic_surgery_procedure
  const otherType =
    details.other_beauty_procedure || details.other_cosmetic_surgery_procedure

  return (
    type && {
      type,
      ...(otherType && { otherType }),
    }
  )
}

const parseHistory = (json) => {
  let history = json[json.type]?.selected_auspicious_date_history || []
  const supportedHistoryTypes = [
    'cosmetic_surgery',
    'beauty',
    'study_abroad',
    'new_pet',
    'product_launch',
  ]
  let mapHistory = history.map((it) => {
    // convert single object to multiple object
    const auspicious_dates = it.auspicious_dates || [it.auspicious_date]
    const normalizeAuspiciousDates = auspicious_dates.map((auspicious_date) => {
      const key = Object.keys(auspicious_date).find((it) =>
        supportedHistoryTypes.includes(it)
      )
      return {
        ...auspicious_date, // Copy all existing properties
        [key]: {
          auspicious_date: auspicious_date[key],
        },
      }
    })
    /* restructure the data to parseAuspiciousDates
      Eg format =>  {
        type: <String>,
        [type]: {
          auspicious_dates: [
            {
              [type]:{
                auspicious_date: {
                  date: <String>,
                  start_time: <String>,
                  end_time: <String>,
                }
              },
              [details_key]: {
                ...
              }
            }
          ]
        }
      }
    */
    const data = {
      type: json.type,
      [json.type]: {
        auspicious_dates: normalizeAuspiciousDates,
      },
    }
    return parseAuspiciousDates(data, 'auspicious_dates')
  })
  return mapHistory
}

const formatDateSelectionDetails = async function (
  json,
  owners,
  confirmationInfo
) {
  let selectedDates =
    parseAuspiciousDate(json, 'admin_selected_date') ||
    parseAuspiciousDates(json, 'admin_selected_dates')
  let suggestedDates = parseAuspiciousDates(json, 'auto_selected_dates')
  const allDates = [
    ...selectedDates.map((it) => ({ ...it, selected: true })),
    ...suggestedDates.map((it) => {
      return {
        ...it,
      }
    }),
  ]
    .sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time))
    .map((it, index) => ({
      ...it,
      title: `${it.title} ${index + 1}`,
      index: index + 1,
    }))
  selectedDates = allDates.filter((it) => it.selected)
  suggestedDates = allDates.filter((it) => !it.selected)

  const suggestedColors = await getSuggestedColors(json)
  const selectedColors = parseSelectedColors(json)

  // map selected auto dates for product launch
  const history = parseHistory(json).map((e) => {
    const history = e.map((date) => {
      let index = allDates.findIndex(
        (sg) =>
          `${sg.start_date_time}${sg.end_date_time}` ==
          `${date.start_date_time}${date.end_date_time}`
      )
      return {
        ...date,
        index: allDates[index]?.index,
      }
    })
    return history
  })

  const order = ['morning', 'noon', 'evening', 'midnight']
  const orderMap = Object.fromEntries(order.map((v, i) => [v, i]))

  return {
    id: json.id,
    requestNumber: json.display_id,
    requestType: json.type,
    status: json.status,
    requestDate: moment(json.created_at).format('DD MMM YYYY • HH:mm'),
    userId: json.user_id,
    price: json.amount,
    note: json.additional_info,
    requestPeriod: json.preferred_date_ranges.map(
      ({ start_date, end_date }) =>
        `${moment(start_date).format('DD MMMM YYYY')}  to  ${moment(end_date).format('DD MMMM YYYY')}`
    ),
    timePeriod: json.time_of_days
      ?.sort((a, b) => orderMap[a] - orderMap[b])
      .map((t) => timeOfDays[t]),
    isWeekDays: json.day_types.includes('weekday'),
    isWeekend: json.day_types.includes('weekend'),
    additionalImages: json.attachment_urls || [],
    infoItems: [
      { label: 'Name', value: json.contact_name },
      { label: 'Phone Number', value: json.contact_phone },
      {
        label: 'Address',
        value:
          (json?.address &&
            [
              json.address.house_no,
              json.address.village,
              json.address.sub_district,
              json.address.district,
              json.address.province,
              json.address.postal_code,
            ].join(', ')) ||
          '',
      },
    ],
    paymentItems: [
      {
        label: 'Transaction ID',
        value: json.payment_detail.charge_body?.chargeId,
        copy: true,
      },
      { label: 'Payment Link', value: json.payment_link, open: true },
      {
        label: 'Payment Status',
        value: json.payment_detail.status,
        copy: false,
      },
      {
        label: 'Payment Date',
        value: moment(json.payment_detail.created_at).format(
          'DD MMM YYYY • HH:mm'
        ),
        copy: false,
      },
      {
        label: 'Payment Method',
        value:
          json.payment_detail.charge_body?.paymentMethod?.paymentMethodType,
        copy: false,
      },
      {
        label: 'Amount',
        value: `${json.payment_detail.amount / 100} ${json.payment_detail.currency}`,
        copy: false,
      },
    ],
    suggestedDates,
    selectedDates,
    owners,
    confirmationInfo,
    suggestedNumbers: getSuggestedNumbers(json),
    selectedNumbers: getSelectedNumbers(json),
    suggestedColors,
    selectedColors,
    suggestion: json.remark,
    isAutoMode: json.is_auto_select,
    surgeryInfo: parseSurgeryInfo(json),
    studyInfo: parseStudyInfo(json),
    studyGift: parseStudyGift(json),
    newPetInfo: parsePetInfo(json),
    newPetGift: parsePetGift(json),
    productLaunchInfo: parseProductInfo(json),
    productLaunchGift: parseProductGift(json),
    surgeryTypeInfo: parseSurgeryTypeInfo(json),
    history: history,
  }
}

const getSelectedNumbers = (json) => {
  if (json.type === 'new_car') {
    return json[json.type].lucky_numbers || []
  }

  return []
}

const getSuggestedColors = async (json) => {
  if (json.type === 'new_car') {
    const { data } = await calendarAxios.get('/colors')
    return [
      {
        label: 'Colors',
        colors: data,
      },
    ]
  }

  return []
}

const parseSelectedColors = (json) => {
  const type = json.type
  if (type === 'new_car') {
    const [one, two] = json[type].lucky_colors
    return {
      auspiciousOne: one || null,
      auspiciousTwo: two || null,
      inauspiciousName: json[type].strongest_element,
    }
  }

  return {
    auspiciousOne: null,
    auspiciousTwo: null,
    inauspiciousName: json[type]?.strongest_element || null,
  }
}

const getSuggestedNumbers = (json) => {
  if (json.type === 'new_car') {
    return new Array(10).fill(null).map((_, index) => index)
  }

  return []
}

export default {
  state,
  mutations,
  actions,
  getters,
}
